<script lang="ts" setup>
import { z } from 'zod'
import type { FormSubmitEvent, Form } from '#ui/types'

interface Props {
  token?: string
}

const props = defineProps<Props>()

const { t } = useI18n()
const { login } = useSanctumAuth()

const showPassword = ref(false)

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
  remember: z.boolean(),
})

type Schema = z.output<typeof schema>

const form = ref<Form<Schema>>()
const state = reactive<Schema>({
  email: '',
  password: '',
  remember: false,
})

async function onSubmit(event: FormSubmitEvent<Schema>) {
  form.value?.clear()

  const credentials = event.data

  try {
    await login(credentials)
  } catch (error) {
    const err = useSanctumError(error)

    if (err.isValidationError) {
      form.value?.setErrors(err.bag)
    }
  }
}
</script>

<template>
  <UForm
    ref="form"
    :schema="schema"
    :state="state"
    class="space-y-4 sm:min-w-80 md:min-w-96 max-w-md"
    @submit="onSubmit"
  >
    <span v-if="props.token" class="text-sm text-green-500">
      {{ t('auth.password_reset_success') }}
    </span>

    <UFormField :label="t('fields.email')" name="email">
      <UInput
        v-model="state.email"
        icon="i-heroicons-at-symbol"
        autocomplete="username"
        class="w-full"
        trailing
      />
    </UFormField>

    <UFormField :label="t('fields.password')" name="password">
      <UInput
        v-model="state.password"
        :type="showPassword ? 'text' : 'password'"
        autocomplete="current-password"
        icon="i-heroicons-lock-closed"
        class="w-full"
        :ui="{ trailing: 'pe-1' }"
      >
        <template #trailing>
          <UButton
            color="neutral"
            variant="link"
            size="sm"
            :icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
            :aria-label="showPassword ? t('common.hide_password') : t('common.show_password')"
            :aria-pressed="showPassword"
            @click="showPassword = !showPassword"
          />
        </template>
      </UInput>
    </UFormField>

    <UFormField name="remember_me">
      <UCheckbox
        v-model="state.remember"
        name="remember_me"
        :label="t('auth.remember_me')"
      />
    </UFormField>

    <div class="flex justify-end gap-4 items-center">
      <ULink
        to="/auth/forgot-password"
        class="text-sm text-neutral-500 underline"
      >
        {{ t('auth.forgot_password') }}
      </ULink>

      <UButton type="submit">
        {{ t('auth.login') }}
      </UButton>
    </div>
  </UForm>
</template>
